import React from 'react';
import { SortAsc, SortDesc } from 'lucide-react';
import TableFooter from './TableFooter';

const DataTable = ({
  columns,
  data,
  loading,
  currentPage,
  totalPages,
  itemsPerPage,
  totalItems,
  onPageChange,
  onItemsPerPageChange,
  sortBy,
  sortOrder,
  onSort,
  itemLabel = 'items',
  itemsPerPageOptions = [10, 20, 50, 100],
  showFooter = true,
  className = '',
  tableClassName = '',
  onRowClick
}) => {
  // Opens the row, except when the click landed on a control inside it (which has
  // its own action) or ended a text selection (e.g. copying a sale number).
  const handleRowClick = (e, row) => {
    if (e.target.closest('button, a, input, select, textarea, label')) return;
    if (window.getSelection()?.toString()) return;
    onRowClick(row, e);
  };

  // The same paging bar goes above and below the table; both drive the same page state.
  const renderPaging = (placement) => (
    <TableFooter
      currentPage={currentPage}
      totalPages={totalPages}
      itemsPerPage={itemsPerPage}
      totalItems={totalItems}
      onPageChange={onPageChange}
      onItemsPerPageChange={onItemsPerPageChange}
      itemsPerPageOptions={itemsPerPageOptions}
      itemLabel={itemLabel}
      placement={placement}
    />
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center p-8">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className={className}>
      {showFooter && renderPaging('top')}
      <div className={`bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden ${tableClassName}`}>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                {columns.map((column) => (
                  <th
                    key={column.key}
                    className={`px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider ${
                      column.sortable ? 'cursor-pointer hover:bg-gray-100' : ''
                    }`}
                    onClick={column.sortable ? () => onSort(column.key) : undefined}
                  >
                    <div className="flex items-center gap-2">
                      {column.title}
                      {column.sortable && sortBy === column.key && (
                        sortOrder === 'asc' ? <SortAsc className="h-4 w-4" /> : <SortDesc className="h-4 w-4" />
                      )}
                      {column.sortable && sortBy !== column.key && (
                        <div className="h-4 w-4" />
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {data.map((row, index) => (
                <tr
                  key={row.id || index}
                  className={`hover:bg-gray-50 ${onRowClick ? 'cursor-pointer' : ''}`}
                  onClick={onRowClick ? (e) => handleRowClick(e, row) : undefined}
                >
                  {columns.map((column) => (
                    <td key={column.key} className="px-6 py-4">
                      {column.render ? column.render(row[column.key], row, index) : row[column.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {showFooter && renderPaging('bottom')}
    </div>
  );
};

export default DataTable;
